import styled from "@emotion/styled";
import { Trans } from "@lingui/react/macro";
import Head from "next/head";
import React from "react";
import { PageTitle } from "../page-title";
import { TogglHireLogo } from "./ui";

interface ErrorPageProps {
  message: React.ReactNode;
  content?: React.ReactNode;
}

export function ErrorPage({ message, content }: ErrorPageProps) {
  return (
    <PageContainer>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <PageTitle />

      <TogglHireLogo />

      <PageContent>
        <ErrorContent>
          <ErrorTitle>
            <Trans comment="An error has happened">Oops!</Trans>
          </ErrorTitle>
          <ErrorMessage>{message}</ErrorMessage>
        </ErrorContent>
        {content && <CustomContent>{content}</CustomContent>}
      </PageContent>
    </PageContainer>
  );
}

const PageContainer = styled.main`
  display: grid;
  gap: 24px;
  padding: 24px 16px;

  @media (min-width: 768px) {
    gap: 32px;
    padding: 32px;
  }
`;

const PageContent = styled.div`
  place-self: flex-start center;
  width: clamp(200px, 100%, 520px);
  border-radius: 16px;
  overflow: hidden;
`;

const ErrorContent = styled.div`
  color: ${(props) => props.theme.typography.colorPrimary};
  background-color: ${(props) => props.theme.colors.white};
  padding: 36px 36px 52px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;

  @media (min-width: 768px) {
    padding: 78px 68px 46px;
    gap: 4px;

    :last-child {
      padding-bottom: 78px;
    }
  }
`;

const ErrorTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
`;

const ErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
`;

const CustomContent = styled.div`
  background-color: ${(props) => props.theme.colors.purple[5]};
  padding: 36px;

  @media (min-width: 768px) {
    padding: 36px 68px;
  }
`;
